// ---------------------------------------------------------------------------
// The two read-only panels: what the server is doing, and what the AI opponents
// have cost.
//
// Split out of `AdminScreen.tsx`. They are together because neither one can
// change anything — props in, figures out, no callback back to the screen — so
// this is the half of the admin page with no destructive action anywhere in it.
// ---------------------------------------------------------------------------

import type { AdminLlmUsage, AdminMetrics } from '@countdown/shared';
import { Card, CardTitle } from '../../components/primitives/index.js';
import { bytes, uptime } from './atoms.js';

export function Metrics({ metrics }: { metrics: AdminMetrics }) {
  const figures: [string, string][] = [
    ['rooms', String(metrics.rooms)],
    ['games running', String(metrics.activeGames)],
    ['players', String(metrics.players)],
    ['spectators', String(metrics.spectators)],
    ['bots', String(metrics.bots)],
    ['uptime', uptime(metrics.uptimeSeconds)],
    ['rss', bytes(metrics.rssBytes)],
    ['heap', bytes(metrics.heapUsedBytes)],
    ['database', bytes(metrics.dbSizeBytes)],
    ['dictionary', `${metrics.dictionaryWords.toLocaleString()} words`],
  ];

  return (
    <Card>
      <CardTitle>Metrics</CardTitle>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {figures.map(([label, value]) => (
          <div key={label}>
            <dt className="label-mono">{label}</dt>
            <dd className="tabular mt-1 font-mono text-lg font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/**
 * What the AI opponents have cost this month, and who ran it up.
 *
 * Usernames, never Clerk ids: the ids are PRIVATE and the question is "who",
 * which a username answers. `null` is a guest-era or deleted account rather
 * than a gap to fill in with an id.
 */
export function LlmUsagePanel({ usage }: { usage: AdminLlmUsage }) {
  const usd = (value: number) => `$${value.toFixed(4)}`;
  const spentPercent = usage.budgetUsd > 0 ? (usage.spentUsd / usage.budgetUsd) * 100 : 0;

  return (
    <Card>
      <CardTitle>AI spend this month</CardTitle>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <div>
          <dt className="label-mono">spent</dt>
          <dd
            className={`tabular mt-1 font-mono text-lg font-semibold ${usage.exhausted ? 'text-alert' : ''}`}
          >
            {usd(usage.spentUsd)}
          </dd>
        </div>
        <div>
          <dt className="label-mono">budget</dt>
          <dd className="tabular mt-1 font-mono text-lg font-semibold">{usd(usage.budgetUsd)}</dd>
        </div>
        <div>
          <dt className="label-mono">remaining</dt>
          <dd className="tabular mt-1 font-mono text-lg font-semibold">
            {usd(usage.remainingUsd)}
          </dd>
        </div>
        {usage.exhausted ? (
          <p className="text-[13px] text-alert">
            Spent. No new AI opponent can be seated until the month turns over. Raise
            LLM_MONTHLY_BUDGET_USD to reopen it sooner.
          </p>
        ) : null}
      </div>

      <div
        aria-hidden="true"
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-raised"
        data-testid="llm-budget-bar"
      >
        <div
          className={`h-full ${usage.exhausted ? 'bg-alert' : 'bg-blue-500'}`}
          style={{ width: `${Math.min(100, spentPercent)}%` }}
        />
      </div>

      {usage.byModel.length === 0 ? (
        <p className="mt-3 text-[13px] text-ink-dim">Nothing spent yet this month.</p>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <h4 className="label-mono">by model</h4>
            <ul className="mt-2 space-y-1">
              {usage.byModel.map((row) => (
                <li key={row.model} className="flex items-baseline gap-2 text-[13px]">
                  <span className="min-w-0 flex-1 truncate font-mono">{row.model}</span>
                  <span className="tabular font-mono text-ink-dim">
                    {row.calls} {row.calls === 1 ? 'call' : 'calls'}
                  </span>
                  {/* Failures are worth surfacing next to the money: a model
                      billing steadily while answering nothing is the shape of a
                      token cap set too low, not of people playing a lot. */}
                  {row.failures > 0 ? (
                    <span className="tabular font-mono text-alert">{row.failures} failed</span>
                  ) : null}
                  <span className="tabular font-mono font-semibold">{usd(row.costUsd)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="label-mono">by account</h4>
            <ul className="mt-2 space-y-1">
              {usage.topSpenders.map((row, index) => (
                <li
                  key={row.username ?? `unknown-${index}`}
                  className="flex items-baseline gap-2 text-[13px]"
                >
                  <span className="min-w-0 flex-1 truncate font-mono">
                    {row.username === null ? '(account gone)' : `@${row.username}`}
                  </span>
                  <span className="tabular font-mono text-ink-dim">
                    {row.calls} {row.calls === 1 ? 'call' : 'calls'}
                  </span>
                  <span className="tabular font-mono font-semibold">{usd(row.costUsd)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}
